import { capitalize, range, sum } from 'remeda';
import type { Granularity, PeriodTotals } from 'shared/hooks/use-load-reminders-and-collections';
import type { ApplicationLocale } from 'types';

export const CHART_MARGIN = { top: 42, right: 55, bottom: 56, left: 55 };

export const BAR_PADDING = 0.1;

export const toBandColumn = (count: number, index: number) => {
  const step = 100 / (count + BAR_PADDING);

  return { left: (BAR_PADDING + index) * step, width: (1 - BAR_PADDING) * step };
};

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const LAST_DAY_OF_WEEK = 6;

const toWeekLabel = (week: string, locale: ApplicationLocale) => {
  const firstDay = new Date(week);
  const lastDay = new Date(firstDay.getTime() + LAST_DAY_OF_WEEK * MILLISECONDS_PER_DAY);

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).formatRange(firstDay, lastDay);
};

const toMonthLabel = (bucket: string, locale: ApplicationLocale) =>
  capitalize(
    new Date(bucket).toLocaleDateString(locale, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }),
  );

export const toBucketLabel = (
  bucket: string,
  locale: ApplicationLocale,
  granularity: Granularity,
) => (granularity === 'month' ? toMonthLabel(bucket, locale) : toWeekLabel(bucket, locale));

const toReminderTotal = (reminders: PeriodTotals['reminders']) => sum(Object.values(reminders));

const FAINTEST_EFFORT = 0.1;

const toIntensity = (fraction: number) => FAINTEST_EFFORT + fraction * (1 - FAINTEST_EFFORT);

export const toScaleIntensities = (steps: number): ReadonlyArray<number> =>
  range(0, steps).map(step => toIntensity(step / (steps - 1)));

export const toEffortCells = (periods: ReadonlyArray<PeriodTotals>) => {
  const totals = periods.map(({ reminders }) => toReminderTotal(reminders));
  const busiest = Math.max(...totals, 0);

  return periods.map(({ period }, index) => ({
    period,
    total: totals[index],
    intensity: toIntensity(busiest === 0 ? 0 : totals[index] / busiest),
  }));
};

const monthOf = (bucket: string) => bucket.slice(0, 7);

export const toMonthStartPeriods = (periods: ReadonlyArray<PeriodTotals>): ReadonlyArray<string> =>
  periods
    .filter(
      ({ period }, index) => index === 0 || monthOf(period) !== monthOf(periods[index - 1].period),
    )
    .map(({ period }) => period);

const yearOf = (bucket: string) => bucket.slice(0, 4);

export const toYearStartBuckets = (buckets: ReadonlyArray<PeriodTotals>): ReadonlyArray<string> =>
  buckets
    .filter(
      ({ period }, index) => index === 0 || yearOf(period) !== yearOf(buckets[index - 1].period),
    )
    .map(({ period }) => period);

export const toShortMonth = (bucket: string, locale: ApplicationLocale) =>
  capitalize(new Date(bucket).toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' }));
